import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  CLIENT_IMAGE_CSS_VARS,
  CLIENT_IMAGE_FILES,
  ClientImageAsset,
  DEFAULT_CLIENT,
  DEFAULT_IMAGE_FOLDER,
  getClienteApiUrl,
  getClientImagePath,
  getContentBasePath,
  resolvePublicAssetPath,
} from './client-path.util';

@Injectable({ providedIn: 'root' })
export class ClientLoaderService {
  /** Cliente para i18n (/i18n/{name}/) */
  private clientName = DEFAULT_CLIENT;
  /** Carpeta de imágenes estáticas (/content/images/{carpeta}/) */
  private imageFolder = DEFAULT_IMAGE_FOLDER;
  private clientData: Record<string, string> = {};
  private clientLoaded = false;
  private clientLoadedPromise!: Promise<void>;
  private resolveClientLoaded!: () => void;

  constructor(private http: HttpClient) {
    this.clientLoadedPromise = new Promise(resolve => {
      this.resolveClientLoaded = resolve;
    });
  }

  async loadClient(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<Record<string, string>>(getClienteApiUrl())
      );
      const clientName = response['name'] || response['carpeta'] || DEFAULT_CLIENT;
      const themeFallback = await this.fetchThemeFile(clientName);
      this.procesarDatosCliente({ ...themeFallback, ...response });
    } catch (error) {
      console.warn('Backend no responde. Activando respaldo estatico...', error);
      const storedName = localStorage.getItem('clientName');
      const storedCarpeta = localStorage.getItem('imageFolder');
      this.clientName = storedName || DEFAULT_CLIENT;
      this.imageFolder = storedCarpeta || storedName || DEFAULT_IMAGE_FOLDER;
      await this.loadThemeFromFile(this.clientName);
    } finally {
      this.clientLoaded = true;
      this.resolveClientLoaded();
    }
  }

  private procesarDatosCliente(data: Record<string, string>): void {
    this.clientData = { ...data };
    this.clientName = data['name'] || data['carpeta'] || DEFAULT_CLIENT;
    this.imageFolder = data['carpeta'] || this.clientName;
    this.setFavicon(this.imageFolder);
    this.setDocumentTitle(data['title-head'] || data['TITLE_HEAD']);
    localStorage.setItem('clientName', this.clientName);
    localStorage.setItem('imageFolder', this.imageFolder);
    localStorage.setItem('login-type', data['login-type'] ?? 'clave');
    this.inyectarEstilos(data, this.imageFolder);
  }

  private async fetchThemeFile(clientName: string): Promise<Record<string, string>> {
    const ruta = `${getContentBasePath()}/themes/${clientName}.json`;
    try {
      return await firstValueFrom(this.http.get<Record<string, string>>(ruta));
    } catch {
      return {};
    }
  }

  private async loadThemeFromFile(clientName: string): Promise<void> {
    const themeData = await this.fetchThemeFile(clientName);
    if (Object.keys(themeData).length === 0) {
      console.error('Error critico: fallo backend y JSON de respaldo.', clientName);
      if (clientName !== 'default') {
        await this.loadThemeFromFile('default');
      }
      return;
    }
    themeData['name'] = clientName;
    this.procesarDatosCliente(themeData);
  }

  private normalizeTheme(data: Record<string, string>): Record<string, string> {
    const normalized = { ...data };
    const aliasPairs: Array<[string, string]> = [
      ['--blue', '--accent'],
      ['--blue-light', '--accent-light'],
      ['--blue-pale', '--accent-pale'],
      ['--blue-paler', '--accent-paler'],
    ];
    for (const [legacy, semantic] of aliasPairs) {
      if (normalized[legacy] && !normalized[semantic]) {
        normalized[semantic] = normalized[legacy];
      }
      if (normalized[semantic] && !normalized[legacy]) {
        normalized[legacy] = normalized[semantic];
      }
    }
    return normalized;
  }

  /** Variables semánticas derivadas de la paleta del cliente */
  private injectDerivedTokens(theme: Record<string, string>, root: HTMLElement): void {
    const navy = theme['--navy'];
    const accent = theme['--accent'] || theme['--blue'];
    const accentLight = theme['--accent-light'] || theme['--blue-light'];
    const accentPale = theme['--accent-pale'] || theme['--blue-pale'];
    const accentPaler = theme['--accent-paler'] || theme['--blue-paler'];
    const footerBg = theme['--footer-bg'] || navy;
    const pdfColor = theme['pdf_primary_color'];

    if (navy) {
      root.style.setProperty('--header-bg', navy);
    }
    if (accent && !theme['--btn-action-bg']) {
      root.style.setProperty('--btn-action-bg', accent);
    }
    if (accentLight && !theme['--btn-action-hover']) {
      root.style.setProperty('--btn-action-hover', accentLight);
    }
    if (accent && !theme['--link']) {
      root.style.setProperty('--link', accent);
    }
    if (accentPaler && !theme['--card-tint-1']) {
      root.style.setProperty('--card-tint-1', accentPaler);
    }
    if (accentPale && !theme['--card-tint-2']) {
      root.style.setProperty('--card-tint-2', accentPale);
    }
    if (accentLight && !theme['--card-tint-3']) {
      root.style.setProperty('--card-tint-3', accentLight);
    }
    if (theme['--muted'] && !theme['--card-tint-4']) {
      root.style.setProperty('--card-tint-4', theme['--muted']);
    }
    if (footerBg) {
      root.style.setProperty('--footer-bg', footerBg);
    }
    if (pdfColor) {
      root.style.setProperty('--pdf-primary-color', pdfColor);
    }
  }

  private inyectarEstilos(themeData: Record<string, string>, imageFolder: string): void {
    const root = document.documentElement;
    const theme = this.normalizeTheme(themeData);

    Object.entries(theme).forEach(([key, value]) => {
      if (key.startsWith('--url-')) {
        return;
      }
      if (key.startsWith('--')) {
        root.style.setProperty(key, value);
      }
    });

    this.injectDerivedTokens(theme, root);
    this.inyectarImagenesCliente(imageFolder);
  }

  /** Resuelve logos desde /content/images/{carpeta}/ con nombres fijos */
  private inyectarImagenesCliente(imageFolder: string): void {
    const root = document.documentElement;
    for (const [cssVar, assetKey] of Object.entries(CLIENT_IMAGE_CSS_VARS)) {
      const fileName = CLIENT_IMAGE_FILES[assetKey];
      root.style.setProperty(
        cssVar,
        `url('${getClientImagePath(imageFolder, encodeURIComponent(fileName))}')`
      );
    }
  }

  async getClientName(): Promise<string> {
    if (!this.clientLoaded) {
      await this.clientLoadedPromise;
    }
    return this.clientName;
  }

  /** GMU no expone catálogo ni gestión de trámites en la home */
  async hasTramitesCatalog(): Promise<boolean> {
    const client = await this.getClientName();
    return client !== 'gmu';
  }

  hasTramitesCatalogSync(): boolean {
    return this.clientName !== 'gmu';
  }

  /** GMU no expone autorizaciones pendientes en el área de usuario */
  hasAutorizacionesPendientesSync(): boolean {
    return this.clientName !== 'gmu';
  }

  getImageFolder(): string {
    return this.imageFolder;
  }

  async getConfig(key: string, fallback = ''): Promise<string> {
    if (!this.clientLoaded) {
      await this.clientLoadedPromise;
    }
    return this.clientData[key] ?? fallback;
  }

  getConfigSync(key: string, fallback = ''): string {
    return this.clientData[key] ?? fallback;
  }

  isFeatureEnabled(key: string): boolean {
    const value = this.getConfigSync(key, 'false');
    return value === 'true' || value === '1';
  }

  getImageUrl(fileName: string, imageFolder?: string): string {
    return getClientImagePath(imageFolder ?? this.imageFolder, fileName);
  }

  /** URL de un asset estándar del cliente (logo4, logo-panel, etc.) */
  getClientImage(asset: ClientImageAsset, imageFolder?: string): string {
    return this.getImageUrl(CLIENT_IMAGE_FILES[asset], imageFolder);
  }

  /** URL de un asset estático configurado en tema/API (footer, certificados, etc.) */
  getAssetUrl(path: string): string {
    return resolvePublicAssetPath(path);
  }

  getLoginType(): string {
    return localStorage.getItem('login-type') ?? 'clave';
  }

  setFavicon(imageFolder?: string): void {
    const folder = imageFolder ?? this.imageFolder;
    const head = document.head;
    let link: HTMLLinkElement | null = head.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      head.appendChild(link);
    }
    link.href = getClientImagePath(folder, CLIENT_IMAGE_FILES.favicon);
  }

  private setDocumentTitle(title?: string): void {
    if (!title) {
      return;
    }
    document.title = title;
    const titleEl = document.getElementById('tituloDoc');
    if (titleEl) {
      titleEl.innerHTML = title;
    }
  }
}
